import React from 'react'
import { Crown } from 'lucide-react'

export interface RoleBadgeProps {
  role: string
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
}

export const getRoleBadge = (role: string, size: 'sm' | 'md' | 'lg' = 'md', showIcon: boolean = true) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm'
  }

  const iconSizes = {
    sm: 'h-3 w-3 mr-1',
    md: 'h-3 w-3 mr-1',
    lg: 'h-4 w-4 mr-1.5'
  }

  const baseClasses = `inline-flex items-center rounded-full font-medium ${sizeClasses[size]}`

  if (role === 'ADMIN') {
    return (
      <span className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`}>
        {showIcon && <Crown className={iconSizes[size]} />}
        Admin
      </span>
    )
  }

  if (role === 'TENANT_ADMIN') {
    return (
      <span className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`}>
        {showIcon && <Crown className={iconSizes[size]} />}
        Owner
      </span>
    )
  }

  if (role === 'PENTESTER') {
    return (
      <span className={`${baseClasses} bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200`}>
        {showIcon && <Crown className={iconSizes[size]} />}
        Pentester
      </span>
    )
  }

  if (role === 'TENANT_USER') {
    return (
      <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`}>
        User
      </span>
    )
  }

  // Default fallback
  return (
    <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`}>
      User
    </span>
  )
}

export const getRoleBadgeFromRoles = (roles: string[] | Array<{ _id: string; name: string }>, size: 'sm' | 'md' | 'lg' = 'md', showIcon: boolean = true) => {
  // Handle both string array and object array formats
  const roleNames = Array.isArray(roles) 
    ? roles.map(role => typeof role === 'string' ? role : (role?.name || '')).filter(name => name !== '')
    : []

  // Priority order: ADMIN > TENANT_ADMIN > PENTESTER > TENANT_USER
  if (roleNames.includes('ADMIN')) {
    return getRoleBadge('ADMIN', size, showIcon)
  }
  if (roleNames.includes('TENANT_ADMIN')) {
    return getRoleBadge('TENANT_ADMIN', size, showIcon)
  }
  if (roleNames.includes('PENTESTER')) {
    return getRoleBadge('PENTESTER', size, showIcon)
  }
  if (roleNames.includes('TENANT_USER')) {
    return getRoleBadge('TENANT_USER', size, showIcon)
  }

  // Default fallback
  return getRoleBadge('MEMBER', size, showIcon)
}

// React component version
export const RoleBadge: React.FC<RoleBadgeProps> = ({ role, size = 'md', showIcon = true }) => {
  return <>{getRoleBadge(role, size, showIcon)}</>
}

export const RoleBadgeFromRoles: React.FC<{ 
  roles: string[] | Array<{ _id: string; name: string }>
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean 
}> = ({ roles, size = 'md', showIcon = true }) => {
  return <>{getRoleBadgeFromRoles(roles, size, showIcon)}</>
}
